import Button from '@/common/components/base/button/Button';
import MobileInput from '@/common/components/base/input/mobileInput/MobileInput';
import TextInput from '@/common/components/base/input/textInput/TextInput';
import { PHONE_NUMBER_RULE } from '@/common/utils/rules/phoneNumber.rules';
import { POSTAL_CODE_RULE } from '@/common/utils/rules/postalCode.rules';
import { requiredText } from '@/common/utils/rules/publicRules';
import { ADJUSTER_REPORT_DAMAGED_LINKS } from '@/modules/adjusterReportModules/adjusterReport/links/damaged/damagedLinks';
import { getCulpritUserInformationQueryOption } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getCulpritUserInformation/getCulpritUserInformationQueryOptions';
import { getDamagedUserInformationQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getDamagedUserInformation/getDamagedUserInformationQueryOptions';
import { PatchDamagedContactInformationQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/patchDamagedContactInformation/patchDamagedContactInformationQueryOptions';
import { ContactInformationOwnerType } from '@/modules/adjusterReportModules/IncidentParticipants/types/OwnerType';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, Flex } from 'styled-system/jsx';

export const OwnerContract = () => {
  const [searchParams] = useSearchParams();
  const { id, damagedId } = useParams();
  const navigate = useNavigate();

  const { data } = useQuery(
    searchParams.get('type') === 'culprit'
      ? getCulpritUserInformationQueryOption(+id!)
      : getDamagedUserInformationQueryOptions(+id!, +damagedId!),
  );
  const mutate = useMutation(PatchDamagedContactInformationQueryOptions());

  const methods = useForm<ContactInformationOwnerType>({
    mode: 'onChange',
  });
  const phoneNumber = useWatch({
    control: methods.control,
    name: 'phone',
  });
  const submitHandler = () => {
    const temporaryObject: ContactInformationOwnerType = {
      address:
        methods.getValues('address') === ''
          ? null
          : methods.getValues('address'),
      postal_code:
        methods.getValues('postal_code') === ''
          ? null
          : methods.getValues('postal_code'),
      phone:
        methods.getValues('phone') === '' ? null : methods.getValues('phone'),
      city_code:
        methods.getValues('city_code') === ''
          ? null
          : methods.getValues('city_code'),
      phone_number:
        methods.getValues('phone_number') === ''
          ? null
          : methods.getValues('phone_number'),
    };

    mutate.mutate(
      { id: id!, formData: temporaryObject, damaged_id: damagedId! },
      {
        onSuccess: () => {
          navigate(-1);
        },
      },
    );
  };

  useEffect(() => {
    if (data) {
      methods.reset({
        ...data.contact_information,
      });
    }
  }, [data]);
  const previousStep = () => {
    navigate(
      ADJUSTER_REPORT_DAMAGED_LINKS.DAMAGED_INFORMATION({
        damagedId: +damagedId!,
        id: +id!,
      }).DRIVER_OWNER_INFORMATION.owner,
      { replace: true },
    );
  };
  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(submitHandler)}>
        <Flex flexDirection="column" gap="20px">
          <MobileInput
            name="phone_number"
            label="تلفن همراه"
            rules={{
              required: requiredText,
            }}
          />
          <Box display="flex" flexDirection="row" gap="10px">
            <Box flexBasis={'80%'}>
              <TextInput
                name="phone"
                label="تلفن ثابت"
                placeholder="XX-XX-XX-XX"
                direction="ltr"
                key={'phone'}
                noCharInput={true}
                rules={{ ...PHONE_NUMBER_RULE, required: requiredText }}
                max={8}
              />
            </Box>
            <Box flexBasis={'30%'}>
              <TextInput
                name="city_code"
                key={'city_code'}
                placeholder="کد شهر"
                direction="ltr"
                noCharInput={true}
                rules={{
                  minLength: { message: 'پیش شماره شهر 3 رقمی است', value: 3 },
                  required: {
                    message: requiredText,
                    value: phoneNumber ? true : false,
                  },
                }}
                max={3}
              />
            </Box>
          </Box>
          <TextInput
            name="postal_code"
            label="کد پستی"
            optionalLabel
            key={'economic_code'}
            noCharInput
            rules={POSTAL_CODE_RULE}
          />
          <TextInput
            name="address"
            label="ادرس"
            optionalLabel
            key={'address'}
          />
          <Flex w="100%" gap={16} mb="1rem">
            <Button fullWidth variant="outlined" onClick={previousStep}>
              مرحله قبل
            </Button>{' '}
            <Button
              onClick={methods.handleSubmit(submitHandler)}
              type="submit"
              width="100%"
              disabled={!methods?.formState?.isValid}
            >
              تائید و ادامه
            </Button>
          </Flex>
        </Flex>
      </form>
    </FormProvider>
  );
};
